import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getCategoryName } from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEOHead from '@/components/base/SEOHead';
import { categories, allArticles } from '@/mocks/articles';

const CATEGORY_IMAGE = "https://readdy.ai/api/search-image?query=An%20abstract%20warm%20toned%20security%20and%20privacy%20concept%20with%20soft%20overlapping%20geometric%20shapes%20minimal%20clean%20design%20gentle%20amber%20and%20cream%20gradient%20modern%20editorial%20style%20high%20detail&width=1800&height=600&seq=category-header-safety&orientation=landscape";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryArticles = allArticles.filter((a) => a.category === slug);
  const catName = category.name;
  const catDesc = category.description;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${catName} - 安全指南`,
    description: catDesc,
    url: `https://safety-guide.example.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://safety-guide.example.com/article/${a.slug}`,
        name: a.title,
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${catName} - 成人网站安全使用完全指南`}
        description={catDesc}
        keywords={`成人网站,${catName},成人网站安全访问,正规成人网站推荐,成人网站隐私保护`}
        canonicalUrl={`https://safety-guide.example.com/category/${slug}`}
        ogImage={CATEGORY_IMAGE}
        schemaJson={schema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[280px] md:h-[360px] flex items-center justify-center overflow-hidden">
            <img
              src={CATEGORY_IMAGE}
              alt={catName}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/25" />
            <div className="relative z-10 w-full text-center px-4 md:px-6 pt-16">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
                {catName}
              </h1>
              <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto leading-relaxed">
                {catDesc}
              </p>
            </div>
          </section>

          <section className="py-10 md:py-14">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <div className="mb-8">
                <Breadcrumb
                  items={[
                    { label: '首页', path: '/' },
                    { label: catName },
                  ]}
                />
              </div>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.slug} {...article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-foreground-600 mb-4">该分类暂无文章</p>
                  <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
                </div>
              )}
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                其他分类
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {categories
                  .filter((c) => c.slug !== slug)
                  .map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className="flex items-center gap-2.5 px-4 py-3 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200/60 transition-all text-sm text-foreground-700 hover:text-primary-600 whitespace-nowrap"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className={`${c.icon} text-sm`} />
                      </span>
                      {c.name}
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}